import type { CSSProperties, ReactNode, Ref } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Chip, IconButton, styled, type SxProps, type Theme, Tooltip } from '@mui/material';
import type { Profile } from 'api';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';

export interface PersonnelRoleCardProps {
  label: string;
  name?: string;
  imageSrc?: string;
  avatarData?: Profile[];
  isLoading?: boolean;
  onEdit?: () => void;
  editDisabled?: boolean;
  editTooltip?: string;
  editButtonRef?: Ref<HTMLButtonElement>;
  editPlacement?: 'header' | 'footer';
  variant?: 'default' | 'emphasized';
  chipTone?: 'primary' | 'accent';
  headerContent?: ReactNode;
  avatarSx?: SxProps<Theme>;
  avatarStyle?: CSSProperties;
  children?: ReactNode;
}

const getEmphasizedBackground = (theme: Theme) =>
  theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#1B2430';

const CardRoot = styled(Box, {
  shouldForwardProp: prop => prop !== 'cardVariant',
})<{ cardVariant: 'default' | 'emphasized' }>(({ theme, cardVariant }) => {
  const isEmphasized = cardVariant === 'emphasized';

  return {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: theme.spacing(1.5),
    backgroundColor: isEmphasized ? getEmphasizedBackground(theme) : theme.palette.action.hover,
    ...(isEmphasized
      ? {
        // Keep contact name readable on the dark card, but do not override button label colors.
        '& .MuiTypography-root': {
          color: theme.palette.common.white,
        },
        '& .MuiButton-root .MuiTypography-root': {
          color: theme.palette.primary.main,
        },
      }
      : {}),
  };
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
});

const HeaderStart = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  minWidth: 0,
}));

const FooterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginTop: 'auto',
});

const RoleChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'chipTone',
})<{ chipTone: 'primary' | 'accent' }>(({ theme, chipTone }) => {
  if (chipTone === 'accent') {
    return {
      height: 28,
      borderRadius: 999,
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(167, 139, 250, 0.2)' : '#EDE7F6',
      color: theme.palette.mode === 'dark' ? '#C4B5FD' : '#7C3AED',
      fontWeight: 600,
      fontSize: '0.8125rem',
      '& .MuiChip-icon': {
        color: theme.palette.mode === 'dark' ? '#C4B5FD' : '#7C3AED',
        marginLeft: theme.spacing(0.75),
      },
      '& .MuiChip-label': {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(1.25),
      },
    };
  }

  return {
    height: 28,
    borderRadius: 999,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(3, 125, 232, 0.16)' : '#E8F3FC',
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '0.8125rem',
    '& .MuiChip-icon': {
      color: theme.palette.primary.main,
      marginLeft: theme.spacing(0.75),
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1.25),
    },
  };
});

const getDefaultEditBackground = (theme: Theme) =>
  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';

const EditButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'cardVariant',
})<{ cardVariant: 'default' | 'emphasized' }>(({ theme, cardVariant }) => {
  const isEmphasized = cardVariant === 'emphasized';

  return {
    width: 28,
    height: 28,
    borderRadius: theme.spacing(0.75),
    backgroundColor: isEmphasized ? 'rgba(255, 255, 255, 0.12)' : getDefaultEditBackground(theme),
    color: isEmphasized ? theme.palette.common.white : theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: isEmphasized ? 'rgba(255, 255, 255, 0.2)' : theme.palette.action.selected,
    },
  };
});

const PersonnelRoleCard = ({
  label,
  name,
  imageSrc,
  avatarData,
  isLoading = false,
  onEdit,
  editDisabled = false,
  editTooltip,
  editButtonRef,
  editPlacement = 'header',
  variant = 'default',
  chipTone = 'primary',
  headerContent,
  avatarSx,
  avatarStyle,
  children,
}: PersonnelRoleCardProps) => {
  const editControl = onEdit ? (
    <EditButton
      ref={editButtonRef}
      cardVariant={variant}
      size="small"
      aria-label={editTooltip || `Edit ${label}`}
      id={`personnel-role-card-edit-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onEdit}
      disabled={editDisabled}>
      <EditOutlinedIcon sx={{ fontSize: 16 }} />
    </EditButton>
  ) : null;

  const wrappedEditControl =
    editControl &&
    (editTooltip ? (
      <Tooltip title={editTooltip}>
        <span>{editControl}</span>
      </Tooltip>
    ) : (
      editControl
    ));

  const avatarContent = children || (
    <Avatar
      data={avatarData ?? [{ picture_url: imageSrc || '' }]}
      name={name || '-'}
      isLoading={isLoading}
      sx={avatarSx}
      style={avatarStyle}
    />
  );

  return (
    <CardRoot cardVariant={variant}>
      <HeaderRow>
        <HeaderStart>
          <RoleChip
            size="small"
            chipTone={chipTone}
            icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            label={label}
          />
          {headerContent}
        </HeaderStart>
        {editPlacement === 'header' ? wrappedEditControl : null}
      </HeaderRow>
      {editPlacement === 'footer' ? (
        <FooterRow>
          {avatarContent}
          {wrappedEditControl}
        </FooterRow>
      ) : (
        avatarContent
      )}
    </CardRoot>
  );
};

export default PersonnelRoleCard;
